import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { fetchMe } from "./auth";
import { brokerPortalUrl } from "./hostRouting";

/**
 * Step 17 — route guard for the AI application (ai.<host>).
 * Mirror of RequireBroker: same shape, same source of truth
 * (`me.global_nav.is_ai`), but inverted target.
 *
 * States:
 *   - not authenticated → /login (AI login page inside the AI shell)
 *   - authenticated but not AI → hard redirect to broker/normal host
 *   - authenticated AI user → render children
 *
 * The hard redirect (window.location.href) is deliberate: switching
 * away from the AI subdomain means index.js must re-mount the broker
 * `App`, and only a full navigation does that.
 */
export default function RequireAI({ children }) {
  const location = useLocation();
  // loading | ai | not-ai | unauth
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await fetchMe();
        if (!alive) return;
        if (!me) {
          setStatus("unauth");
          return;
        }
        const isAI = !!(me?.global_nav?.is_ai);
        setStatus(isAI ? "ai" : "not-ai");
      } catch {
        if (alive) setStatus("unauth");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (status === "loading") return null;
  if (status === "unauth") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (status === "not-ai") {
    const url = brokerPortalUrl("/projects");
    if (url && typeof window !== "undefined") {
      // Same-tab hard redirect — cross-subdomain, so react-router
      // Navigate can't help here.
      window.location.href = url;
      return null;
    }
    // Fall back to the AI login if we somehow can't derive a host
    // (shouldn't happen in a normal browser, but avoid rendering
    // AI-only content to a non-AI user).
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}
